import { DotsThreeIcon, GlobeIcon } from '@phosphor-icons/react';
import React, { useEffect, useRef } from 'react';

import { MediaSlot } from './MediaSlot';
import { MissingCreativeBand } from './MissingCreativeBand';
import { PlatformFbLikeIcon, PlatformFbMsgBubbleIcon, PlatformFbShareIcon } from './PlatformIcons';
import { PreviewAvatar } from './PreviewAvatar';
import { PLATFORM_SKINS } from './platform-tokens';
import {
  DEFAULT_PREVIEW_BRAND_COLOR,
  FACEBOOK_FEED_FOLD_LENGTH,
  compactNumber,
  platformNaturalMaxWidth,
  resolveAspectRatio,
} from './types';
import { useCaptionFold } from './useCaptionFold';
import { useCarousel } from './useCarousel';
import { useResolvedDarkMode } from './useResolvedDarkMode';

import { cn } from '@/lib/utils';

/**
 * Card width / media height measured directly off the Figma reference
 * (`facebook-post`, single/light, node `28:11226`): 338px card, 331px media.
 * Recorded as a ratio, not copied verbatim, because the CARD is full-width
 * here — this constant only fixes how tall the full-bleed media region is
 * relative to its own width, matching the Figma's ~55%-of-card-height,
 * near-square proportion instead of a plain 1:1 guess.
 */
const META_AD_MEDIA_RATIO = 338 / 331;

/**
 * Mini-card media ratio inside a CAROUSEL card, measured off the Figma
 * carousel frames (`28:11229` light / `28:11228` dark): each card is 170px
 * wide, 241px tall total, with a fixed 48px footer band — so the image
 * region is 170×193, a portrait-leaning ratio, not the near-square single-
 * image ratio above. Corrected from the pre-Figma reconstruction, which
 * reused `META_AD_MEDIA_RATIO` for both.
 */
const META_AD_CAROUSEL_CARD_MEDIA_RATIO = 170 / 193;

/**
 * The carousel-card footer band and the single-image link block share a
 * FOURTH platform colour the Figma file calls "Facebook/Dark/Grey" /
 * light has no named swatch beyond "Facebook/Light/Light grey" — confirmed
 * directly off the pulled frames (single dark `28:11227`, both carousel
 * frames): `#F0F2F5` light, `#333436` dark. `PLATFORM_SKINS`'s 7-role
 * schema doesn't carry a role for this (it sits between `cardBackground`
 * and `cardBorder`), so it's recorded here rather than approximated via
 * `color-mix()` against `cardBorder` — the pre-Figma reconstruction's
 * approximation (`color-mix(cardBorder 25%, cardBackground)`) lands close
 * but not exact (~#F3F3F4 light, ~#47494B dark vs. the real values).
 *
 * The literals themselves live in `src/index.css` as
 * `--platform-facebook-link-band-light`/`-dark` (real `:root`, not
 * `@theme` — see that block's comment), not inline here: this is genuinely
 * new platform-chrome data, same tier as `--object-tile-*`, so it gets a
 * real custom property instead of a second hardcoded copy.
 */
function metaAdLinkBandBackground(theme: 'light' | 'dark'): string {
  return theme === 'dark'
    ? 'var(--platform-facebook-link-band-dark)'
    : 'var(--platform-facebook-link-band-light)';
}

/**
 * The carousel mini-card's description line uses a DIFFERENT text colour
 * than the single-image link block's description, per theme — confirmed on
 * both carousel frames: light matches `skin.secondaryText` (`#65676B`)
 * exactly, but dark uses `#B0B3B8` (`skin.cardBorder`'s value), not
 * `skin.secondaryText` (`#E5E6EB`). Not a typo to normalize — it's what the
 * design actually does in both pulled dark frames (single-dark's own
 * primary/secondary text are identical too; this is a second, independent
 * spot where the two themes don't mirror each other's role mapping).
 */
function metaAdCarouselDescriptionColor(
  theme: 'light' | 'dark',
  skin: { secondaryText: string; cardBorder: string },
): string {
  return theme === 'dark' ? skin.cardBorder : skin.secondaryText;
}

/**
 * One card in a Meta CAROUSEL ad. Each card is its own creative + its own
 * link block — a real Meta carousel format, not a slideshow through one
 * link block. `headline` falls back to the ad's top-level headline when a
 * card doesn't override it (the common case: same headline, different
 * product shots), so callers aren't forced to repeat it per card.
 */
export interface MetaAdCarouselItem {
  imageUrl?: string | null;
  headline?: string;
  description?: string;
  cta?: string;
}

/**
 * Meta in-feed SPONSORED ad — the paid sibling of `FacebookPost`. Same
 * header and action chrome; what makes an ad an ad: "Sponsored" replaces
 * the timestamp, a link-preview block sits under the media (destination ·
 * headline · description · CTA), and — per this replica's Figma rebuild —
 * the media is FULL-BLEED and dominant, the single biggest visual
 * difference from the pre-rebuild version. There is no engagement row
 * unless real metrics were passed in; an unpublished ad has none to show.
 *
 * Renders the platform's OWN Facebook skin (`PLATFORM_SKINS.facebook`),
 * not Mora's semantic tokens — see `platform-tokens.ts`'s file header and
 * `docs/planning/GOAL-AD-PREVIEW-SYSTEM-2026-08-21.md` §2 for the ruling
 * this licenses. Mora's active theme selects WHICH skin variant renders
 * (light ⇒ `dark:false`, dark ⇒ `dark:true`); pass `theme` to force a
 * specific variant regardless of Mora's theme (tests, the style guide
 * showing both side by side).
 *
 * All four `facebook-post` variants are now pulled and verified against the
 * Figma file `Dl0gE7lH27N7x7OJb0DFBw` (single light `28:11226`, single dark
 * `28:11227`, carousel light `28:11229`, carousel dark `28:11228`) — none
 * are unverified reconstructions any more. Single/light was the original
 * port and needed no correction. Single/dark needed one: the link block's
 * background is a fourth platform colour (`metaAdLinkBandBackground`), not
 * a `color-mix()` approximation off `cardBorder`. The carousel needed the
 * most correction — mini-card width (real ratio ~46% of the card, not
 * 72%), media aspect ratio (real cards are portrait-leaning 170:193, not
 * the single-image near-square ratio), footer layout (headline+description
 * beside the CTA in one row, not stacked above it), the per-card
 * description line (accepted by the type, silently dropped by the
 * reconstruction), and the mini-card border (the design has none — cards
 * are set off by the footer-band colour change alone).
 *
 * The OUTER card carries no border either, in light or dark — confirmed
 * directly off both frames' root node (`28:11226`/`28:11227`): no
 * `border`/`stroke` property at all, only a plain `rounded-[8px]` fill.
 * Elevation comes from `shadow-elevation-1` alone, matching
 * `ARTIFACT_PREVIEW_CARD` (`src/lib/ui-tokens.ts`), the same treatment
 * `PostBody`'s own preview pane uses. A `border` + `shadow-elevation-1`
 * pair used to sit on this root — a decoration Figma never specified and a
 * combination this component family uses nowhere else consistently — and
 * read as a "weird" doubled outline (founder report, 2026-08-21). Internal
 * dividers (the `border-t` between sections) are structural, per Figma, and
 * stay; only the OUTER card's border was removed.
 *
 * Deliberately NOT a `PlatformPreviewKind`. The dispatcher maps a stored
 * `posts.platforms` value to a shell; an ad unit never carries one, so
 * adding a kind would grow five `never`-exhaustive switches for a case the
 * dispatcher cannot receive.
 */
export interface MetaAdPreviewProps {
  brandName: string;
  brandColor?: string;
  avatarUrl?: string;
  /** The selected primary-text variant. Folds at Facebook's feed length. */
  primaryText: string;
  /** The selected headline variant — the bold line in the link block. */
  headline: string;
  /** The smaller line under the headline. */
  description?: string;
  /** The CTA button label ("Shop Now", "Learn More"). */
  cta: string;
  /**
   * Destination domain. Mora's `ad_units` schema has no destination URL
   * today, so callers pass null and the slot states that plainly rather
   * than printing a plausible-looking domain nobody entered.
   */
  destinationDomain?: string | null;
  imageUrl?: string | null;
  /** A `single_video` Ad's creative. Takes priority over `imageUrl` when both are present, matching `MediaSlot`'s own video-first resolution. */
  videoUrl?: string | null;
  /**
   * The video's poster/thumbnail (`video_data.image_hash`/`image_url`, M26 —
   * REFERENCES-paid-loop-provider-apis-2026-09-01.md). Optional: Meta's own
   * worked examples pair a thumbnail with `video_id`, but the
   * `AdCreativeVideoData` reference marks no field required and no reachable
   * page states it as a hard requirement (decision D5, same ledger). When it
   * is absent Mora sends no `image_hash` — what Meta renders in its place is
   * not documented on any page in the ledger, so this preview claims nothing
   * about it, and the preview itself is Mora's rendering, not Meta's.
   */
  videoPosterUrl?: string | null;
  aspectRatio?: number | string | null;
  isLoading?: boolean;
  emptyHint?: string;
  /**
   * The filename the missing creative is expected to carry, shown inside the
   * empty band so the slot instructs instead of merely reporting.
   *
   * Pass null whenever the name cannot be derived from real stored values. A
   * naming convention is only worth stating if it is the one the pipeline will
   * actually match on, and a half-guessed name is worse than no name at all.
   */
  expectedFilename?: string | null;
  /** Rendered inside the empty creative slot — the way out of that state. */
  emptyAction?: React.ReactNode;
  /**
   * Real engagement on a PUBLISHED ad only. Omit entirely for an unpublished
   * draft — the reaction row does not render at all without this, matching
   * every other replica in this directory (`engagementTruth.test.tsx`
   * guards it). When present, renders exactly what's given, including zero.
   */
  engagement?: {
    likes?: number;
    comments?: number;
    shares?: number;
  };
  /**
   * Carousel cards — a real Meta ad format, not the single-image default.
   * 2+ items switches the whole media + link-block region to a horizontally
   * scrolling row of mini-cards (Figma `facebook-post`/carousel). Fewer
   * than 2 items is treated as the single-image layout, using `imageUrl`.
   */
  carouselItems?: MetaAdCarouselItem[];
  /** Controlled active carousel index (mirrors `useCarousel`'s contract). */
  carouselActiveIndex?: number;
  onCarouselActiveIndexChange?: (index: number) => void;
  /**
   * Forces which Facebook skin variant renders, independent of Mora's
   * active theme. Omit to follow Mora's theme (the normal, real-app path);
   * pass explicitly for the style guide's side-by-side light/dark
   * comparison and for tests that must render a deterministic variant.
   */
  theme?: 'light' | 'dark';
  /**
   * Optional node layered over the media region only, above the image — the
   * ad-workbench Preview mode's seam for compositing `overlay_layers` (the
   * headline/logo/shape stack the user authored in Design mode) on top of the
   * same creative. Same contract as `PlatformPreviewData.mediaOverlay`; a
   * distinct prop here rather than that shared type because an ad unit has no
   * `PlatformPreviewKind` (see the file header). Never rendered when there is
   * no media slot to sit inside — an ad with no creative shows
   * `MissingCreativeBand` instead, and painting a headline over empty air
   * would be a lie the caller didn't ask for. Ignored on a carousel — each
   * card is its own creative and there is no single media slot to sit inside.
   */
  overlay?: React.ReactNode;
  /**
   * Rendered INSIDE a document frame that already draws the border.
   *
   * Standalone it pins itself to Facebook's natural feed width and centres,
   * which is right when it floats on a page. Embedded, the PARENT owns the
   * width — the caller has already sized a stage for it to sit on.
   *
   * This deliberately does NOT strip the post's border, radius or fill. An
   * earlier pass did, so the replica merged into its container and the founder
   * reported the result exactly: "it feels like that's the card and that's part
   * of the thing… it should feel like a card inside of that card." A post
   * preview has to read as a POST sitting ON something.
   *
   * Default `false` — every existing standalone call site is unchanged.
   */
  embedded?: boolean;
  /**
   * A short context header rendered above the "Sponsored" header row — the
   * minimum honest differentiator for a placement Meta serves the SAME
   * single-image link-ad creative into as plain Feed (Search results,
   * Marketplace), so the card doesn't render byte-identical to Feed with
   * only a caption telling them apart. Omit for plain Feed, which needs no
   * context header.
   */
  contextLabel?: string;
  /**
   * `'side_by_side'` renders a wider card with the link block
   * (destination/headline/description/CTA) beside the media instead of
   * stacked under it. NOT currently claimed to be Facebook Feed's real
   * desktop rendering — an earlier version of this doc comment asserted
   * that as fact with no source; `meta-placement-catalog.ts`'s
   * `facebook-feed-desktop` frame was reverted to the default stacked
   * layout on 2026-09-03 for exactly that reason (no row in
   * `REFERENCES-paid-loop-provider-apis-2026-09-01.md` backs it, and it
   * shrank the previewed creative to ~16% of the card's area). The prop
   * stays available for a caller with a sourced reason to use it. Default
   * `'stacked'` — the existing mobile/default composition, unchanged for
   * every current caller. Ignored for carousel ads (each mini-card keeps
   * its own footer band regardless).
   */
  layout?: 'stacked' | 'side_by_side';
  className?: string;
}

export const MetaAdPreview: React.FC<MetaAdPreviewProps> = ({
  brandName,
  brandColor = DEFAULT_PREVIEW_BRAND_COLOR,
  avatarUrl,
  primaryText,
  headline,
  description,
  cta,
  destinationDomain,
  imageUrl,
  videoUrl,
  videoPosterUrl,
  aspectRatio,
  isLoading = false,
  emptyHint = 'No creative yet — copy only',
  expectedFilename,
  emptyAction,
  engagement,
  carouselItems,
  carouselActiveIndex,
  onCarouselActiveIndexChange,
  theme,
  overlay,
  embedded = false,
  contextLabel,
  layout = 'stacked',
  className,
}) => {
  const isDarkMode = useResolvedDarkMode();
  const resolvedTheme = theme ?? (isDarkMode ? 'dark' : 'light');
  const skin = PLATFORM_SKINS.facebook[resolvedTheme];

  const { visible, needsFold, expanded, setExpanded } = useCaptionFold(
    primaryText,
    FACEBOOK_FEED_FOLD_LENGTH,
  );
  const domain = destinationDomain?.trim();
  const resolvedAspect = resolveAspectRatio(aspectRatio, META_AD_MEDIA_RATIO);
  const validCarouselItems = carouselItems && carouselItems.length >= 2 ? carouselItems : undefined;
  const isCarousel = !!validCarouselItems;

  const {
    carouselOverlay,
    safeActive,
    slides: carouselSlides,
  } = useCarousel(
    validCarouselItems?.map((item) => item.imageUrl ?? ''),
    imageUrl,
    { activeIndex: carouselActiveIndex, onActiveIndexChange: onCarouselActiveIndexChange },
  );

  // The carousel row shows every card at once (a real Meta carousel scrolls
  // through simultaneously-visible mini-cards, unlike a single swapped
  // image) — but the ACTIVE-INDEX state, clamping, and prev/next overlay
  // are all `useCarousel`, the same hook the organic posts use. This effect
  // is the only new wiring: when the shared hook's index changes (its own
  // buttons, or a controlled `carouselActiveIndex` from the caller), scroll
  // the corresponding physical card into view.
  const cardRefs = useRef<Array<HTMLDivElement | null>>([]);
  useEffect(() => {
    if (!isCarousel) return;
    cardRefs.current[safeActive]?.scrollIntoView({
      behavior: 'smooth',
      inline: 'center',
      block: 'nearest',
    });
  }, [isCarousel, safeActive]);

  // An ad with no creative is the NORMAL state of a copy-only ad unit, so the
  // slot must be sized to what it actually says — one sentence — not to the
  // square an image would have occupied. The band keeps the ad's shape
  // legible (header → text → media → link block) without pretending a
  // creative exists.
  const hasMedia = isCarousel || Boolean(imageUrl) || Boolean(videoUrl) || isLoading;
  const isSideBySide = layout === 'side_by_side' && !isCarousel;
  const likes = engagement?.likes;
  const comments = engagement?.comments;
  const shares = engagement?.shares;
  const hasEngagement = likes != null || comments != null || shares != null;

  return (
    <div
      className={cn(embedded ? 'w-full min-w-0' : 'mx-auto w-full', className)}
      style={embedded ? undefined : { maxWidth: `${platformNaturalMaxWidth('facebook-post')}px` }}
    >
      <article
        className="w-full overflow-hidden rounded-lg shadow-elevation-1"
        data-platform="meta-ad"
        style={{ backgroundColor: skin.cardBackground }}
      >
        {contextLabel ? (
          <div
            className="border-b px-3 py-1.5 text-detail font-medium"
            style={{ color: skin.secondaryText, borderColor: skin.cardBorder }}
          >
            {contextLabel}
          </div>
        ) : null}
        <header className="flex items-start justify-between gap-3 px-3 pb-2 pt-3">
          <div className="flex min-w-0 items-center gap-2.5">
            <PreviewAvatar
              brandName={brandName}
              brandColor={brandColor}
              avatarUrl={avatarUrl}
              className="size-9 rounded-full"
              textClassName="text-caption font-semibold"
            />
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold" style={{ color: skin.primaryText }}>
                {brandName}
              </p>
              <div
                className="flex items-center gap-1 text-detail"
                style={{ color: skin.secondaryText }}
              >
                <span>Sponsored</span>
                <span className="opacity-40">·</span>
                <GlobeIcon className="size-3" weight="fill" aria-label="Public" />
              </div>
            </div>
          </div>
          <DotsThreeIcon
            className="size-5 shrink-0"
            weight="bold"
            style={{ color: skin.secondaryText }}
          />
        </header>

        {primaryText ? (
          <div className="px-3 pb-3">
            <p
              className="whitespace-pre-wrap break-words text-sm leading-relaxed"
              style={{ color: skin.primaryText }}
            >
              {visible}
              {needsFold && !expanded ? (
                <>
                  {'… '}
                  <button
                    type="button"
                    onClick={() => setExpanded(true)}
                    className="underline-offset-2 transition-opacity hover:opacity-80 hover:underline"
                    style={{ color: skin.secondaryText }}
                  >
                    See more
                  </button>
                </>
              ) : null}
            </p>
          </div>
        ) : null}

        {isCarousel ? (
          <div className="relative">
            <div
              className="flex w-full snap-x snap-mandatory gap-3 overflow-x-auto px-3 pb-3 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
              role="list"
              aria-label={`${brandName} ad, ${carouselSlides?.length ?? 0} cards`}
            >
              {validCarouselItems.map((item, index) => (
                <div
                  key={index}
                  ref={(el) => {
                    cardRefs.current[index] = el;
                  }}
                  role="listitem"
                  className="flex w-[46%] max-w-[190px] shrink-0 snap-start flex-col overflow-hidden rounded"
                >
                  <MediaSlot
                    ratio={META_AD_CAROUSEL_CARD_MEDIA_RATIO}
                    bg="muted"
                    imageUrl={item.imageUrl}
                    alt={item.headline || headline || `${brandName} ad creative ${index + 1}`}
                  />
                  {/*
                    Corrected from a vertical stack (headline over a
                    below-it CTA pill) to the Figma's actual row layout:
                    headline + description stacked on the left, the CTA
                    pinned to the right, both vertically centered in a
                    fixed 48px band (`21:2014`/`21:2228` in the light/dark
                    carousel frames). `item.description` was previously
                    accepted by the type but never rendered here — dropped
                    silently by the reconstruction.
                  */}
                  <div
                    className="flex h-12 items-center gap-1 p-2"
                    style={{ backgroundColor: metaAdLinkBandBackground(resolvedTheme) }}
                  >
                    <div className="min-w-0 flex-1">
                      <p
                        className="truncate text-detail font-semibold leading-snug"
                        style={{ color: skin.primaryText }}
                      >
                        {item.headline || headline || 'No headline'}
                      </p>
                      {item.description?.trim() ? (
                        <p
                          className="truncate text-micro leading-snug"
                          style={{
                            color: metaAdCarouselDescriptionColor(resolvedTheme, skin),
                          }}
                        >
                          {item.description.trim()}
                        </p>
                      ) : null}
                    </div>
                    {(item.cta ?? cta)?.trim() ? (
                      <span
                        className="inline-flex w-fit shrink-0 items-center rounded px-2.5 py-1 text-micro font-medium"
                        style={{ backgroundColor: skin.ctaBackground, color: skin.ctaText }}
                      >
                        {(item.cta ?? cta).trim()}
                      </span>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>
            {carouselOverlay}
          </div>
        ) : isSideBySide ? (
          // Facebook Feed's real DESKTOP rendering: media and link block sit
          // side by side in a wider row instead of stacked — see `layout`'s
          // doc comment. This is what genuinely distinguishes the desktop
          // Feed frame from the mobile one, rather than the same stacked
          // card under two different captions.
          <div className="flex items-stretch">
            <div className="w-2/5 shrink-0">
              {hasMedia ? (
                <MediaSlot
                  ratio={resolvedAspect}
                  bg="muted"
                  imageUrl={imageUrl}
                  videoUrl={videoUrl}
                  videoPoster={videoPosterUrl}
                  isLoading={isLoading}
                  emptyHint={emptyHint}
                  alt={headline || `${brandName} ad creative`}
                  overlay={overlay}
                />
              ) : (
                <MissingCreativeBand
                  hint={emptyHint}
                  expectedFilename={expectedFilename}
                  action={emptyAction}
                  aspectRatio={META_AD_MEDIA_RATIO}
                />
              )}
            </div>
            <div
              className="flex min-w-0 flex-1 flex-col justify-center gap-1 px-3 py-2"
              style={{ backgroundColor: metaAdLinkBandBackground(resolvedTheme) }}
            >
              <p
                className={cn(
                  'truncate font-medium',
                  domain ? 'text-micro uppercase tracking-wider' : 'text-[13px] leading-5',
                )}
                style={{ color: skin.secondaryText, opacity: domain ? 1 : 0.85 }}
              >
                {domain || 'No destination URL yet'}
              </p>
              <p
                className="line-clamp-2 text-sm font-semibold leading-snug"
                style={{ color: skin.primaryText }}
              >
                {headline || 'No headline'}
              </p>
              {description?.trim() ? (
                <p className="line-clamp-1 text-detail" style={{ color: skin.secondaryText }}>
                  {description.trim()}
                </p>
              ) : null}
              {cta.trim() ? (
                <span
                  className="mt-1 inline-flex w-fit shrink-0 items-center rounded-md px-3 py-1.5 text-detail font-medium"
                  style={{ backgroundColor: skin.ctaBackground, color: skin.ctaText }}
                >
                  {cta.trim()}
                </span>
              ) : null}
            </div>
          </div>
        ) : (
          <>
            {hasMedia ? (
              <MediaSlot
                ratio={resolvedAspect}
                bg="muted"
                imageUrl={imageUrl}
                videoUrl={videoUrl}
                videoPoster={videoPosterUrl}
                isLoading={isLoading}
                emptyHint={emptyHint}
                alt={headline || `${brandName} ad creative`}
                overlay={overlay}
              />
            ) : (
              <MissingCreativeBand
                hint={emptyHint}
                expectedFilename={expectedFilename}
                action={emptyAction}
                aspectRatio={META_AD_MEDIA_RATIO}
              />
            )}

            <div
              className="flex items-center gap-3 px-3 py-2"
              style={{ backgroundColor: metaAdLinkBandBackground(resolvedTheme) }}
            >
              <div className="min-w-0 flex-1">
                <p
                  className={cn(
                    'truncate font-medium',
                    domain ? 'text-micro uppercase tracking-wider' : 'text-[13px] leading-5',
                  )}
                  style={{ color: skin.secondaryText, opacity: domain ? 1 : 0.85 }}
                >
                  {domain || 'No destination URL yet'}
                </p>
                <p
                  className="mt-0.5 line-clamp-2 text-sm font-semibold leading-snug"
                  style={{ color: skin.primaryText }}
                >
                  {headline || 'No headline'}
                </p>
                {description?.trim() ? (
                  <p
                    className="mt-0.5 line-clamp-1 text-detail"
                    style={{ color: skin.secondaryText }}
                  >
                    {description.trim()}
                  </p>
                ) : null}
              </div>
              {cta.trim() ? (
                <span
                  className="inline-flex shrink-0 items-center rounded-md px-3 py-1.5 text-detail font-medium"
                  style={{ backgroundColor: skin.ctaBackground, color: skin.ctaText }}
                >
                  {cta.trim()}
                </span>
              ) : null}
            </div>
          </>
        )}

        {hasEngagement ? (
          <div
            className="flex items-center justify-between border-t px-3 py-2 text-detail"
            style={{ color: skin.secondaryText, borderColor: skin.cardBorder }}
          >
            {likes != null ? (
              <div className="flex items-center gap-1.5">
                {/* Facebook's own reaction-badge — real Meta chrome, not
                    theme-dependent. The badge fill is Facebook brand blue,
                    which already has an app-wide token (`identity.ts` /
                    `--color-platform-facebook`), so it's applied via the
                    real `bg-platform-facebook` utility, not a literal. The
                    icon inside stays white-on-blue regardless of Mora's
                    theme, same reasoning as `FacebookPost.tsx`'s identical
                    badge — that one value isn't a Mora surface token. */}
                <span className="flex size-4 items-center justify-center rounded-full bg-platform-facebook">
                  {/* eslint-disable-next-line no-restricted-syntax -- see comment above */}
                  <PlatformFbLikeIcon size={9} className="text-white" />
                </span>
                <span>{compactNumber(likes)}</span>
              </div>
            ) : (
              <span />
            )}
            <div className="flex items-center gap-3">
              {comments != null ? <span>{compactNumber(comments)} comments</span> : null}
              {shares != null ? <span>{compactNumber(shares)} shares</span> : null}
            </div>
          </div>
        ) : null}

        {/* Facebook's own glyphs (Like / Comment / Share), not
            `FacebookActionBar`'s Phosphor stand-ins — that component is built
            for the organic `FacebookPost`, which still follows Mora's
            semantic tokens (`text-foreground/70`). This ad replica follows
            the platform skin instead, so it needs icons and colour that
            resolve the same way. */}
        <div className="grid grid-cols-3 px-1 py-0.5" style={{ color: skin.primaryText }}>
          <MetaAdAction icon={<PlatformFbLikeIcon size={16} />} label="Like" />
          <MetaAdAction icon={<PlatformFbMsgBubbleIcon size={17} />} label="Comment" />
          <MetaAdAction icon={<PlatformFbShareIcon size={17} />} label="Share" />
        </div>
      </article>
    </div>
  );
};

const MetaAdAction: React.FC<{ icon: React.ReactNode; label: string }> = ({ icon, label }) => (
  <button
    type="button"
    className="flex items-center justify-center gap-2 rounded-md py-2 text-sm font-medium opacity-80 transition-opacity hover:opacity-100"
  >
    {icon}
    <span>{label}</span>
  </button>
);
